import {
  Link2,
  ExternalLink,
  Package,
  Plus,
} from 'lucide-react';
import { cn } from '@/lib/classnames';
import { Button } from '@/components/ui/button';
import type { RelatedItem } from '../../../types';
import {
  RELATIONSHIP_TYPE_LABELS,
  RELATIONSHIP_TYPE_COLORS,
} from '../../../types';

interface RelatedTabProps {
  readonly relatedItems?: RelatedItem[];
  readonly onMerge?: (targetItemId: string) => void;
  readonly onAddRelatedItem?: () => void;
}

export function RelatedTab({
  relatedItems, onMerge, onAddRelatedItem 
}: RelatedTabProps) {
  if (!relatedItems?.length) {
    return (
      <EmptyState
        icon={Link2}
        title="No related items"
        description="Add substitutes or link similar products to help with sourcing flexibility."
        actionLabel="Add Related Item"
        onAction={onAddRelatedItem}
      />
    );
  }

  const groupedByType: Record<string, RelatedItem[]> = {};
  for (const item of relatedItems) {
    const type = item.relationshipType;
    if (!groupedByType[type]) {
      groupedByType[type] = [];
    }
    groupedByType[type].push(item);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Manage product relationships for sourcing flexibility and inventory optimization.
        </p>
        {onAddRelatedItem && (
          <Button variant="outline" size="sm" onClick={onAddRelatedItem}>
            <Plus className="mr-2 h-4 w-4" />
            Add Related Item
          </Button>
        )}
      </div>

      {Object.entries(groupedByType).map(([type, items]) => (
        <RelationshipGroup
          key={type}
          type={type}
          items={items}
          onMerge={onMerge}
        />
      ))}

      {/* Merge Info */}
      <div className="rounded-lg bg-blue-50 p-4">
        <h4 className="font-medium text-blue-900">About Product Merging</h4>
        <p className="mt-1 text-sm text-blue-700">
          Merging combines similar products into a single master item. All recipe references,
          purchase history, and supplier relationships will be consolidated. This action cannot be undone.
        </p>
      </div>
    </div>
  );
}

interface RelationshipGroupProps {
  readonly type: string;
  readonly items: RelatedItem[];
  readonly onMerge?: (targetItemId: string) => void;
}

function RelationshipGroup({
  type, items, onMerge 
}: RelationshipGroupProps) {
  return (
    <div>
      <h3 className="font-medium text-gray-900 mb-3">
        {RELATIONSHIP_TYPE_LABELS[type] ?? type}s ({items.length})
      </h3>
      <div className="space-y-2">
        {items.map((related) => (
          <RelatedItemCard
            key={related.itemId}
            item={related}
            onMerge={onMerge}
          />
        ))}
      </div>
    </div>
  );
}

interface RelatedItemCardProps {
  readonly item: RelatedItem;
  readonly onMerge?: (targetItemId: string) => void;
}

function RelatedItemCard({
  item, onMerge 
}: RelatedItemCardProps) {
  const showMergeButton = onMerge && item.relationshipType === 'similar';

  return (
    <div className="flex items-center justify-between rounded-lg border p-4 hover:bg-gray-50">
      <div className="flex items-center gap-3">
        <span className={cn(
          'inline-flex rounded-full px-2 py-1 text-xs font-medium',
          RELATIONSHIP_TYPE_COLORS[item.relationshipType]
        )}>
          {RELATIONSHIP_TYPE_LABELS[item.relationshipType]}
        </span>
        <div>
          <p className="font-medium text-gray-900">{item.name}</p>
          <p className="text-sm text-gray-500">SKU: {item.sku}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {item.conversionFactor && item.conversionFactor !== 1 && (
          <span className="text-sm text-gray-500">
            Conversion: {item.conversionFactor}x
          </span>
        )}
        {showMergeButton && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onMerge(item.itemId)}
          >
            Merge
          </Button>
        )}
        <ExternalLink className="h-4 w-4 text-gray-400 cursor-pointer hover:text-gray-600" />
      </div>
    </div>
  );
}

interface EmptyStateProps {
  readonly icon: typeof Package;
  readonly title: string;
  readonly description: string;
  readonly actionLabel?: string;
  readonly onAction?: () => void;
}

function EmptyState({
  icon: emptyIcon,
  title,
  description,
  actionLabel,
  onAction,
}: EmptyStateProps) {
  const IconComponent = emptyIcon;
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="rounded-full bg-gray-100 p-4 mb-4">
        <IconComponent className="h-8 w-8 text-gray-400" />
      </div>
      <h3 className="font-medium text-gray-900">{title}</h3>
      <p className="mt-1 text-sm text-gray-500 max-w-sm">{description}</p>
      {actionLabel && (
        <Button variant="outline" size="sm" className="mt-4" onClick={onAction}>
          <Plus className="mr-2 h-4 w-4" />
          {actionLabel}
        </Button>
      )}
    </div>
  );
}
